import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Axios from 'axios';
import { CampaignMemberDto, CampaignMemberStatus } from 'types/globalTypes';
import { errorCatch } from './errorCatcher.reducer';

export enum ReferralCodeValidationStatus {
  Idle = 'idle',
  Loading = 'loading',
  Success = 'success',
  Invalid = 'invalid',
  Expired = 'expired',
  Error = 'error'
}

class ErrorParser {
  taxId: [string] | null;
  legalBusinessName: [string] | null;
  country: [string] | null;
  region: [string] | null;
  city: [string] | null;
  postalCode: [string] | null;

  constructor(
    taxId?: [string],
    name?: [string],
    country?: [string],
    state?: [string],
    city?: [string],
    postalCode?: [string]
  ) {
    this.taxId = taxId || null;
    this.legalBusinessName = name || null;
    this.country = country || null;
    this.region = state || null;
    this.city = city || null;
    this.postalCode = postalCode || null;
  }
}

export type InitState = {
  companyDataValidation: {
    status: string;
    data: companyDataValidationType;
  };
  emailValidation: {
    status: string;
    data: emailValidationType;
  };
  referralCodeValidation: {
    status: ReferralCodeValidationStatus;
    data: CampaignMemberDto | null;
    validatedCode: string | null;
  };
};

export const initialState = {
  companyDataValidation: {
    status: '',
    data: {
      taxId: null,
      legalBusinessName: null,
      country: null,
      region: null,
      city: null,
      postalCode: null
    }
  },
  emailValidation: {
    status: '',
    data: {
      email: null
    }
  },
  referralCodeValidation: {
    status: ReferralCodeValidationStatus.Idle,
    data: null,
    validatedCode: null
  }
} as InitState;

export type companyDataValidationType = {
  taxId: string | null;
  legalBusinessName: string | null;
  country: string | null;
  region: string | null;
  city: string | null;
  postalCode: string | null;
};

type emailValidationType = {
  email: string | null;
};

type companyDataValidationRequest = {
  taxId: string;
  name: string;
  country: string;
  countryIso2: string;
  state: string;
  stateId: string;
  city: string;
  postalCode: string;
  referralCode?: string | null;
};

export const companyDataValidation = createAsyncThunk(
  'validationData/companyData',
  async ({ validationFields, zuoraEnhancedBilling = false }: { validationFields: companyDataValidationRequest, zuoraEnhancedBilling?: boolean }, { dispatch }) => {
    try {
      const path = zuoraEnhancedBilling ? '/api/v2/billing/company/validate' : '/api/v1/billing/company/validate';
      const { data } = await Axios.post(path, validationFields);
      if (data && data.errors) {
        const { taxId, name, country, state, city, postalCode } = data.errors;
        return new ErrorParser(taxId, name, country, state, city, postalCode);
      }
      return data;
    } catch (error) {
      dispatch(errorCatch({ place: 'validationData/companyData.rejected', data: error }));
      return Promise.reject(error);
    }
  }
);

export const emailValidation = createAsyncThunk(
  'validationData/email',
  async (validationFields: emailValidationType, { dispatch }) => {
    try {
      const { data } = await Axios.post('/api/v1/billing/company/users/validate', validationFields);
      if (data && data.errors) {
        return data.errors;
      }
      return data;
    } catch (error) {
      dispatch(errorCatch({ place: 'validationData/email.rejected', data: error }));
      return Promise.reject(error);
    }
  }
);

export const validateReferralCode = createAsyncThunk(
  'validationData/referralCode',
  async (code: string, { dispatch }) => {
    try {
      const { data } = await Axios.get<CampaignMemberDto>(
        `/api/v1/CampaignMembers/referralCodes/${encodeURIComponent(code)}`
      );
      if (data.isDeleted) {
        return { status: ReferralCodeValidationStatus.Invalid, data: null, validatedCode: null };
      }
      if (data.campaignMemberStatus === CampaignMemberStatus.New) {
        return { status: ReferralCodeValidationStatus.Success, data, validatedCode: code };
      }
      if (
        data.campaignMemberStatus === CampaignMemberStatus.Suspended ||
        data.campaignMemberStatus === CampaignMemberStatus.Registered
      ) {
        return { status: ReferralCodeValidationStatus.Expired, data: null, validatedCode: null };
      }
      return { status: ReferralCodeValidationStatus.Invalid, data: null, validatedCode: null };
    } catch (error) {
      if ((error as any)?.response?.status === 404) {
        return { status: ReferralCodeValidationStatus.Invalid, data: null, validatedCode: null };
      }
      dispatch(errorCatch({ place: 'validationData/referralCode.rejected', data: error }));
      return { status: ReferralCodeValidationStatus.Error, data: null, validatedCode: null };
    }
  }
);

const slice = createSlice({
  name: 'validationData',
  initialState,
  reducers: {
    resetCompanyDataValidationState: state => {
      state.companyDataValidation = initialState.companyDataValidation;
    },
    resetEmailValidationState: state => {
      state.emailValidation = initialState.emailValidation;
    },
    resetReferralCodeValidationState: state => {
      state.referralCodeValidation = initialState.referralCodeValidation;
    }
  },
  extraReducers: {
    //company data
    [companyDataValidation.pending.toString()]: state => {
      state.companyDataValidation.data = initialState.companyDataValidation.data;
      state.companyDataValidation.status = 'loading';
    },
    [companyDataValidation.fulfilled.toString()]: (state, action) => {
      state.companyDataValidation.data = action.payload;
      state.companyDataValidation.status = 'success';
    },
    [companyDataValidation.rejected.toString()]: state => {
      state.companyDataValidation.status = 'rejected';
    },
    //email
    [emailValidation.pending.toString()]: state => {
      state.emailValidation.data = initialState.emailValidation.data;
      state.emailValidation.status = 'loading';
    },
    [emailValidation.fulfilled.toString()]: (state, action) => {
      state.emailValidation.data = action.payload;
      state.emailValidation.status = 'success';
    },
    [emailValidation.rejected.toString()]: state => {
      state.emailValidation.data.email = null;
      state.emailValidation.status = 'rejected';
    },
    //referral code
    [validateReferralCode.pending.toString()]: state => {
      state.referralCodeValidation.status = ReferralCodeValidationStatus.Loading;
      state.referralCodeValidation.data = null;
      state.referralCodeValidation.validatedCode = null;
    },
    [validateReferralCode.fulfilled.toString()]: (state, action) => {
      state.referralCodeValidation.status = action.payload.status;
      state.referralCodeValidation.data = action.payload.data;
      state.referralCodeValidation.validatedCode = action.payload.validatedCode;
    },
    [validateReferralCode.rejected.toString()]: state => {
      state.referralCodeValidation.status = ReferralCodeValidationStatus.Error;
      state.referralCodeValidation.data = null;
      state.referralCodeValidation.validatedCode = null;
    }
  }
});

export const {
  resetCompanyDataValidationState,
  resetEmailValidationState,
  resetReferralCodeValidationState
} = slice.actions;
export default slice.reducer;
